import Dexie from 'dexie';

const DB_NAME = 'FigshelfAdminCacheDB';
const DB_VERSION = 1;

const stableStringify = (value) => {
  try {
    if (!value || typeof value !== 'object') return JSON.stringify(value);
    const keys = Object.keys(value).sort();
    const normalized = {};
    for (const k of keys) {
      const v = value[k];
      if (v === undefined) continue;
      normalized[k] = v;
    }
    return JSON.stringify(normalized);
  } catch {
    return String(value);
  }
};

export const makeAdminCacheKey = ({ userId, scope, params }) => {
  return `${userId || 'anon'}:${scope}:${stableStringify(params || {})}`;
};

class AdminCacheDb extends Dexie {
  constructor() {
    super(DB_NAME);

    this.version(DB_VERSION).stores({
      lists: 'key, updatedAt, scope',
    });

    this.lists = this.table('lists');
  }
}

let dbInstance = null;

const isDatabaseClosedError = (error) => {
  if (!error) return false;
  if (error?.name === 'DatabaseClosedError') return true;
  if (typeof error?.message === 'string' && error.message.toLowerCase().includes('database has been closed')) {
    return true;
  }
  if (typeof error?.message === 'string' && error.message.toLowerCase().includes('db.open() was cancelled')) {
    return true;
  }
  return false;
};

const resetAdminCacheDb = () => {
  dbInstance = null;
};

export const getAdminCacheDb = () => {
  if (dbInstance && typeof dbInstance.isOpen === 'function' && !dbInstance.isOpen()) {
    resetAdminCacheDb();
  }

  if (!dbInstance) {
    dbInstance = new AdminCacheDb();
  }

  return dbInstance;
};

export const getCachedAdminList = async (key, { maxAgeMs } = {}) => {
  if (!key) return null;

  const run = async () => {
    const db = getAdminCacheDb();
    const row = await db.lists.get(key);
    if (!row?.data) return null;

    if (maxAgeMs && row.updatedAt) {
      const ageMs = Date.now() - new Date(row.updatedAt).getTime();
      if (Number.isFinite(ageMs) && ageMs > maxAgeMs) return null;
    }

    return row.data;
  };

  try {
    return await run();
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetAdminCacheDb();
      try {
        return await run();
      } catch {
        return null;
      }
    }
    return null;
  }
};

export const cacheAdminList = async (key, payload, { scope } = {}) => {
  if (!key || payload === undefined) return;

  const run = async () => {
    const db = getAdminCacheDb();
    await db.lists.put({
      key,
      scope: scope || null,
      updatedAt: new Date().toISOString(),
      data: payload,
    });
  };

  try {
    await run();
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetAdminCacheDb();
      try {
        await run();
      } catch {
        return;
      }
      return;
    }
  }
};
